/**
 * Turning a raw check-in document into the handful of things an inbox row shows.
 *
 * Everything here is DERIVED, never stored. The alternative was denormalising a
 * summary onto the session so the list could read it directly — but the patient's
 * own client is what writes that document, so denormalised fields would be
 * client-supplied and the rules' update allowlist would need holes punched in it
 * for values the desk then trusts. Deriving costs nothing: the list already has
 * the whole document, because there is nothing else to read.
 */

import { getVisitStartElementDisplay } from "features/visitStart/visitStartElementsCatalog";

/**
 * The one line that tells the desk why this patient is here.
 *
 * The chief-complaint element is an LLM interview whose summary the patient READ
 * AND CONFIRMED before advancing, so quoting it back is quoting them. Prefers the
 * confirmed text over the model's own, for exactly that reason.
 *
 * Returns null when the element is not enabled, not reached, or was skipped —
 * never a placeholder. A row that says "No complaint recorded" reads like a
 * clinical finding; a row with nothing there reads like an unfinished form, which
 * is what it is.
 */
export function checkInChiefComplaint(session) {
  const answer = session?.elementAnswers?.["chief-complaint"];
  if (!answer) return null;
  const text =
    answer.confirmedSummary ||
    answer.summary?.chiefComplaint ||
    answer.summary?.narrative ||
    "";
  const trimmed = String(text).trim();
  return trimmed || null;
}

/**
 * What to call the person, for a list a receptionist reads.
 *
 * A facility cannot read arbitrary `users` documents, so there is no account name
 * to show and this does not pretend otherwise. Everything here is what the patient
 * themselves put on the identity screen.
 *
 * NAME FIRST, then the ID number. This used to be ID-number-only, on the reasoning
 * that a number is what the desk looks a file up by — true, but it made the label
 * unusable in the one case that matters most. The ID number is optional by design
 * ("Leave blank if you do not have your ID with you"), so every check-in from
 * someone who arrived without their card rendered as the literal word "Patient",
 * and a list of rows all reading "Patient" cannot be worked. The identity screen
 * now always carries a name, which is the field a walk-in can always answer.
 *
 * The number is still shown when there is one, because it is what the file is
 * keyed by, and a name alone does not disambiguate two people with the same one.
 *
 * Falls back to "Guest" / "Patient" only for a session that never reached or
 * never answered identity. That still distinguishes the two cases that matter to
 * staff: someone with no account whose answers exist only here, and someone who
 * has a chart the clinician can ask for.
 */
export function checkInPatientLabel(session) {
  const identity = session?.elementAnswers?.identity || {};
  const fullName = String(identity.fullName || "").trim();
  const idNumber = String(identity.idNumber || "").trim();
  if (fullName && idNumber) return `${fullName} · ${idNumber}`;
  if (fullName) return fullName;
  if (idNumber) return idNumber;
  return session?.guest ? "Guest" : "Patient";
}

/**
 * Status as the desk thinks of it, which is not quite the document's `status`.
 *
 * `boot` and `element_loop` are both "still answering" to a receptionist, and the
 * distinction between them is an implementation detail of the shell.
 *
 * The inbox lists only "completed", so this is what keeps a half-answered session
 * out of it. Erring towards in_progress for an unrecognised status is deliberate:
 * a status this code does not know about must not be shown to the desk as a
 * finished check-in.
 */
export function checkInStatus(session) {
  const status = session?.status;
  if (status === "completed") return "completed";
  if (status === "abandoned") return "abandoned";
  return "in_progress";
}

/**
 * Things the patient said they were unsure about or would rather not answer,
 * grouped for display.
 *
 * These are the reason a human is at the desk. "I'm not sure" is not a clinical
 * negative, and the session records it separately from the answers precisely so it
 * cannot be read as one downstream.
 */
export function checkInClarifications(session) {
  const raw = Array.isArray(session?.clarifyingQuestions)
    ? session.clarifyingQuestions
    : [];
  return raw.map((entry) => ({
    ...entry,
    elementLabel:
      getVisitStartElementDisplay(entry?.elementId)?.label || entry?.elementId || "",
  }));
}

/**
 * Which elements this session asked, with their labels, in the order asked.
 * Used by the detail view to render sections even for elements the patient never
 * reached — an unanswered question is information too.
 */
export function checkInElements(session) {
  const enabled = Array.isArray(session?.enabledElements)
    ? session.enabledElements
    : [];
  const answers = session?.elementAnswers || {};
  return enabled.map((elementId) => {
    const display = getVisitStartElementDisplay(elementId) || {};
    return {
      elementId,
      label: display.label || elementId,
      shortLabel: display.shortLabel || display.label || elementId,
      answer: answers[elementId],
      answered: answers[elementId] !== undefined,
    };
  });
}
